import { useState, useEffect, useMemo, useRef } from "react";
import {
	Box,
	Checkbox,
	Chip,
	InputAdornment,
	TextField,
	Typography,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TableComponent, {
	IColumnProps,
} from "../../../components/TableComponent";
import { IPaginationParams } from "../../../services/types";
import { t } from "i18next";
import { useDebounce } from "../../../services/SchoolTransportation/hooks/useDebounce";
import { useLocations } from "./LocationContext";
import type { ILocationProps } from "../../../services/Location/types";

interface LocationListProps {
	onSelect: (id: string) => void;
	selectedIds: string[];
	setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
}

type ConnectionFilter = "all" | "online" | "offline";

function resolveVehicleConnectionStatus(
	location: ILocationProps,
): "online" | "offline" {
	if (typeof location.isOnline === "boolean") {
		return location.isOnline ? "online" : "offline";
	}

	if (!location.timestamp) {
		return "offline";
	}

	const lastTimestamp = new Date(location.timestamp).getTime();
	if (!Number.isFinite(lastTimestamp)) {
		return "offline";
	}

	return Date.now() - lastTimestamp <= 5 * 60 * 1000 ? "online" : "offline";
}

function splitAddressAfterCity(address?: string) {
	const normalized = address?.trim();

	if (!normalized) {
		return {
			firstLine: t("map.searchingAddress"),
			secondLine: null as string | null,
		};
	}

	const citySeparatorIndex = normalized.indexOf(" - ");
	if (citySeparatorIndex < 0) {
		return { firstLine: normalized, secondLine: null as string | null };
	}

	return {
		firstLine: normalized.slice(0, citySeparatorIndex),
		secondLine: normalized.slice(citySeparatorIndex + 3),
	};
}

function getPlatePresentation(plate?: string) {
	const normalized = plate?.trim();

	if (!normalized) {
		return { display: "-", isOldModel: false };
	}

	const upperText = normalized.toUpperCase();
	const plateMatch = upperText.match(/([A-Z]{3})[\s-]?([A-Z0-9]{4})/);

	if (plateMatch) {
		const base = `${plateMatch[1]}${plateMatch[2]}`;
		const fifthChar = base[4];
		const isOldModel = /\d/.test(fifthChar);

		return {
			display: `${base.slice(0, 3)}-${base.slice(3, 7)}`,
			isOldModel,
		};
	}

	const alphanumeric = upperText.replace(/[^A-Z0-9]/g, "");

	if (!alphanumeric) {
		return { display: "-", isOldModel: false };
	}

	const base = alphanumeric.slice(0, 7);

	if (base.length <= 3) {
		return { display: base, isOldModel: false };
	}

	const isOldModel = base.length >= 5 && /\d/.test(base[4]);

	return {
		display: `${base.slice(0, 3)}-${base.slice(3)}`,
		isOldModel,
	};
}

const LocationList = ({
	onSelect,
	selectedIds,
	setSelectedIds,
}: LocationListProps) => {
	const [pagination, setPagination] = useState<IPaginationParams>({
		pageNumber: 1,
		pageSize: 10,
	});

	const [searchTerm, setSearchTerm] = useState("");
	const [connectionFilter, setConnectionFilter] =
		useState<ConnectionFilter>("all");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const hasManualSelectionChange = useRef(false);

	const locations = useLocations();
	const locationIds = useMemo(
		() => Array.from(new Set(locations.map((l) => l.entityId).filter(Boolean))),
		[locations],
	);

	useEffect(() => {
		if (locationIds.length === 0 || hasManualSelectionChange.current) {
			return;
		}

		setSelectedIds((previousIds) => {
			if (previousIds.length === 0) {
				return locationIds;
			}

			const missingIds = locationIds.filter((id) => !previousIds.includes(id));
			if (missingIds.length === 0) {
				return previousIds;
			}

			return [...previousIds, ...missingIds];
		});
	}, [locationIds, setSelectedIds]);

	const normalizedSearch = debouncedSearchTerm.toLowerCase();

	const filteredLocations = useMemo(() => {
		return locations.filter((loc) => {
			const matchesSearch =
				!normalizedSearch ||
				[loc.entityId, loc.vehiclePlate, loc.modelName, loc.address]
					.filter(Boolean)
					.some((value) =>
						(value as string).toLowerCase().includes(normalizedSearch),
					);

			if (!matchesSearch) {
				return false;
			}

			if (connectionFilter === "all") {
				return true;
			}

			return resolveVehicleConnectionStatus(loc) === connectionFilter;
		});
	}, [locations, normalizedSearch, connectionFilter]);

	const onlineCount = useMemo(
		() =>
			locations.filter(
				(loc) => resolveVehicleConnectionStatus(loc) === "online",
			).length,
		[locations],
	);

	const offlineCount = useMemo(
		() =>
			locations.filter(
				(loc) => resolveVehicleConnectionStatus(loc) === "offline",
			).length,
		[locations],
	);

	const paginatedLocations = useMemo(() => {
		const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
		const endIndex = startIndex + pagination.pageSize;
		return filteredLocations.slice(startIndex, endIndex);
	}, [filteredLocations, pagination.pageNumber, pagination.pageSize]);

	const columns: IColumnProps[] = [
		{
			id: "select",
			label: "",
			width: "56px",
			render: (_value: string, _rowIndex: number, row?: ILocationProps) => {
				const loc = row as ILocationProps;
				return (
					<Checkbox
						size="small"
						sx={{
							p: 0,
							m: 0,
							color: "#64748b",
							"&.Mui-checked": { color: "#38bdf8" },
						}}
						checked={selectedIds.includes(loc.entityId)}
						onChange={(e) => {
							hasManualSelectionChange.current = true;

							if (e.target.checked) {
								setSelectedIds((prev) =>
									prev.includes(loc.entityId) ? prev : [...prev, loc.entityId],
								);
							} else {
								setSelectedIds((prev) =>
									prev.filter((id) => id !== loc.entityId),
								);
							}
						}}
					/>
				);
			},
		},
		{
			id: "vehiclePlate",
			label: t("map.plate"),
			width: "24%",
			render: (value: string, _rowIndex: number, row?: ILocationProps) => {
				const { display: formattedPlate, isOldModel } =
					getPlatePresentation(value);
				const loc = row as ILocationProps;
				return (
					<Box
						component="span"
						sx={{
							cursor: "pointer",
							display: "inline-flex",
							alignItems: "center",
						}}
						onClick={() => {
							onSelect(loc.entityId);
						}}
						onKeyDown={(e) => {
							if (e.key !== "Enter" && e.key !== " ") return;
							e.preventDefault();
							onSelect(loc.entityId);
						}}
						role="button"
						tabIndex={0}
						title={formattedPlate}
					>
						<span
							style={{
								display: "inline-flex",
								flexDirection: "column",
								width: 100,
								border: "2px solid #0f172a",
								borderRadius: 6,
								overflow: "hidden",
								background: isOldModel
									? "linear-gradient(180deg, #f3f4f6 0%, #d1d5db 100%)"
									: "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
								boxShadow: "0 1px 4px rgba(0, 0, 0, 0.28)",
								lineHeight: 1,
							}}
						>
							<span
								style={{
									display: "block",
									height: 9,
									backgroundColor: isOldModel ? "#9ca3af" : "#1d4ed8",
								}}
							/>
							<span
								style={{
									display: "block",
									textAlign: "center",
									color: "#111827",
									fontSize: 12,
									fontWeight: 900,
									letterSpacing: isOldModel ? 1 : 0.8,
									lineHeight: "17px",
									textTransform: "uppercase",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									padding: "1px 4px",
								}}
							>
								{formattedPlate}
							</span>
						</span>
					</Box>
				);
			},
		},
		{
			id: "location",
			label: t("map.address"),
			width: "46%",
			render: (_value: string, _rowIndex: number, row?: any) => {
				const loc = row as ILocationProps;
				const text = loc.address?.trim() || "Endereço não informado";
				const addressLines = splitAddressAfterCity(loc.address);

				return (
					<Box
						title={text}
						sx={{
							display: "block",
							maxWidth: "100%",
						}}
					>
						<Box sx={{ minWidth: 0, lineHeight: 1.25 }}>
							<Typography
								variant="body2"
								sx={{
									color: "#e2e8f0",
									fontWeight: 500,
									overflow: "hidden",
									textOverflow: "ellipsis",
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
								}}
							>
								{addressLines.firstLine}
							</Typography>
							{addressLines.secondLine && (
								<Typography variant="caption" sx={{ color: "#94a3b8" }}>
									{addressLines.secondLine}
								</Typography>
							)}
							{loc.modelName && (
								<Typography
									variant="caption"
									sx={{ color: "#64748b", display: "block" }}
								>
									{loc.modelName}
								</Typography>
							)}
						</Box>
					</Box>
				);
			},
		},
		{
			id: "timestamp",
			label: t("map.date"),
			width: "20%",
			render: (value: string | null) => {
				if (!value) {
					return <span>-</span>;
				}

				const date = new Date(value);
				const dateText = date.toLocaleDateString();
				const timeText = date.toLocaleTimeString();

				return (
					<Box sx={{ lineHeight: 1.15 }}>
						<Typography variant="body2" sx={{ color: "#e2e8f0", fontWeight: 600 }}>
							{dateText}
						</Typography>
						<Typography variant="caption" sx={{ color: "#94a3b8" }}>
							{timeText}
						</Typography>
					</Box>
				);
			},
		},
	];

	return (
		<TableComponent
			title={
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: { xs: 0, sm: 3 },
					}}
				>
					<LocationOnOutlinedIcon sx={{ color: "#60a5fa", fontSize: 26 }} />
					<Typography sx={{ fontWeight: 900, lineHeight: 1.1, fontSize: 22 }}>
						{t("map.locations")}
					</Typography>
				</Box>
			}
			additionalTitleComponent={
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "stretch",
						gap: 0.75,
						justifyContent: "flex-end",
						width: { xs: "100%", sm: 280 },
					}}
				>
					<TextField
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.target.value)}
						placeholder="Pesquisar veículo"
						size="small"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
								</InputAdornment>
							),
						}}
						sx={{
							width: "100%",
							"& .MuiOutlinedInput-root": {
								color: "#e2e8f0",
								backgroundColor: "rgba(15, 23, 42, 0.35)",
								borderRadius: 2,
								"& fieldset": { borderColor: "rgba(148, 163, 184, 0.28)" },
								"&:hover fieldset": {
									borderColor: "rgba(96, 165, 250, 0.65)",
								},
								"&.Mui-focused fieldset": { borderColor: "#60a5fa" },
							},
							"& input::placeholder": {
								color: "#94a3b8",
								opacity: 1,
							},
						}}
					/>
					<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
						<Chip
							size="small"
							label={`Todos (${locations.length})`}
							color={connectionFilter === "all" ? "primary" : "default"}
							onClick={() => setConnectionFilter("all")}
							sx={{ fontWeight: 600, borderRadius: 1.5 }}
						/>
						<Chip
							size="small"
							label={`Online (${onlineCount})`}
							color={connectionFilter === "online" ? "success" : "default"}
							onClick={() => setConnectionFilter("online")}
							sx={{ fontWeight: 600, borderRadius: 1.5 }}
						/>
						<Chip
							size="small"
							label={`Offline (${offlineCount})`}
							color={connectionFilter === "offline" ? "error" : "default"}
							onClick={() => setConnectionFilter("offline")}
							sx={{ fontWeight: 600, borderRadius: 1.5 }}
						/>
					</Box>
				</Box>
			}
			rows={paginatedLocations}
			columns={columns}
			isLoading={false}
			totalCount={filteredLocations.length}
			onChangePageOrRowsPerPage={setPagination}
			height="100%"
		/>
	);
};

export default LocationList;
